import {
	Alert,
	Button,
	PasswordInput,
	Stack,
	Text,
	TextInput
} from "@mantine/core"
import type { LoginError } from "@models/error/login-error"
import {
	IconAlertTriangle,
	IconEye,
	IconEyeOff,
	IconLock
} from "@tabler/icons-react"
import { ERROR_CODES } from "@utils/constants.util"
import { stylizeText } from "@utils/stylizeText"
import { useState } from "react"
import type { FC, FormEvent } from "react"
import styles from "./LoginForm.module.css"
import { eventTrackingItems } from "@utils/eventTracking"

export interface LoginFormData {
	username: string
	password: string
}

export interface LoginFormComponentData {
	username?: string
	password?: string
	signInButton?: string
	unsuccessfulLoginMessage?: string
	attemptsRemaining?: string
	accountLockedHeader?: string
	accountLockedMessage?: string
	qaModeMessage?: string
	[key: string]: string | undefined
}

export interface LoginFormProps {
	onSubmit: (data: LoginFormData) => Promise<void> | void
	error?: LoginError | null
	isLoading?: boolean
	loginFormComponentData?: LoginFormComponentData
	onAssistanceModalOpen?: () => void
}

export const LoginForm: FC<LoginFormProps> = ({
	onSubmit,
	error,
	isLoading = false,
	loginFormComponentData,
	onAssistanceModalOpen
}) => {
	const [username, setUsername] = useState("")
	const [password, setPassword] = useState("")

	const handleSubmit = async (e: FormEvent): Promise<void> => {
		e.preventDefault()
		await onSubmit({ username, password })
	}

	const icon =
		error?.errorCode === ERROR_CODES.ACCOUNT_LOCKED ? (
			<IconLock />
		) : (
			<IconAlertTriangle />
		)

	const getErrorMessage = (error: LoginError) => {
		if (error.errorCode === ERROR_CODES.INVALID_USERNAME) {
			return (
				<Text fz={12}>{loginFormComponentData?.unsuccessfulLoginMessage}</Text>
			)
		}

		if (error.errorCode === ERROR_CODES.INVALID_CREDENTIALS) {
			return (
				<>
					<Text fz={12}>
						{loginFormComponentData?.unsuccessfulLoginMessage}
					</Text>
					<Text fz={12} fw={600}>
						{error.remainingAttempts ?? ""}{" "}
						{loginFormComponentData?.attemptsRemaining}
					</Text>
				</>
			)
		}

		if (error.errorCode === ERROR_CODES.ACCOUNT_LOCKED) {
			return (
				<>
					<Text fz={12}>
						{loginFormComponentData?.accountLockedHeader
							? stylizeText(loginFormComponentData.accountLockedHeader, {
									fz: 12
								})
							: ""}
					</Text>
					<Text fz={12}>
						{loginFormComponentData?.accountLockedMessage
							? stylizeText(loginFormComponentData.accountLockedMessage, {
									onAnchorClick: onAssistanceModalOpen,
									fz: 12
								})
							: ""}
					</Text>
				</>
			)
		}

		if (error.errorCode === ERROR_CODES.QAMODE) {
			return (
				<Text fz={12}>
					{loginFormComponentData?.qaModeMessage || error.message}
				</Text>
			)
		}

		if (error.errorCode === ERROR_CODES.INVALID_TOKEN) {
			return <Text fz={12}>{stylizeText(error.message, { fz: 12 })}</Text>
		}

		return loginFormComponentData?.[error?.errorCode] ?? error.message
	}

	const handleOnLogin = () => {
		eventTrackingItems({
			event_name: "click",
			link_url: `${window.location.href}/home`,
			category: "click",
			interactionType: "login_click",
			navLinkText: loginFormComponentData?.signInButton ?? ""
		})
	}
	return (
		<form onSubmit={handleSubmit} className={styles.loginForm}>
			<Stack gap="lg">
				<TextInput
					placeholder={loginFormComponentData?.username}
					value={username}
					onChange={(event) => setUsername(event.currentTarget.value)}
					variant="login"
					aria-label={`TextInput ${loginFormComponentData?.username}`}
					error={!!error}
					data-qa="ushome.input.username"
					autoFocus
				/>
				<PasswordInput
					placeholder={loginFormComponentData?.password}
					value={password}
					onChange={(event) => setPassword(event.currentTarget.value)}
					variant="login"
					aria-label={`PasswordInput ${loginFormComponentData?.password}`}
					error={!!error}
					visibilityToggleIcon={({ reveal }) =>
						reveal ? (
							<IconEye
								size={20}
								color="var(--mantine-color-royal-21)"
								data-qa="ushome.button.show.password"
							/>
						) : (
							<IconEyeOff
								size={20}
								color="var(--mantine-color-royal-21)"
								data-qa="ushome.button.hide.password"
							/>
						)
					}
					data-qa="ushome.input.password"
				/>
				{error && (
					<Alert variant="filled" color="red" icon={icon} radius={30}>
						{getErrorMessage(error)}
					</Alert>
				)}
				<Button
					type="submit"
					variant="dark-blue"
					size="xl"
					fullWidth
					className={styles.loginButton}
					data-qa="ushome.button.login"
					onClick={handleOnLogin}
				>
					{isLoading ? "Loading..." : loginFormComponentData?.signInButton}
				</Button>
			</Stack>
		</form>
	)
}
